import { useAppDispatch } from "hooks/redux";
import { useTranslation } from "react-i18next";
import { cloneDeep, range } from "lodash";
import {
  IWebsiteBuilderHeadingSettings,
  WebsiteBuilderHeadingLevel,
} from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderHeading";
import React, { useMemo, useState } from "react";
import {
  IWebsiteBuilderPopupOption,
  renderWebsiteBuilderPopupOptions,
} from "../../../../helpers/popup/renderWebsiteBuilderPopupOptions";
import { websiteBuilderUpdateElementSettings } from "redux/actions/websiteBuilder/websiteBuilder";
import {
  useWebsiteBuilderDebouncedUpdate,
  WebsiteBuilderDebouncedUpdateTime,
} from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import { WebsiteBuilderPopupSections } from "../../../../helpers/popup/WebsiteBuilderPopupSections";
import { Select } from "components/select/index";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAlignCenter, faAlignLeft, faAlignRight } from "@fortawesome/pro-regular-svg-icons";
import { WebsiteBuilderPopupRow } from "../../../../helpers/popup/WebsiteBuilderPopupRow";
import Input from "components/form/input/Input";

interface IHeadingSettingsProps {
  id: string;
  settings: IWebsiteBuilderHeadingSettings;
}

type TTrackedHeadingSettings = Omit<IWebsiteBuilderHeadingSettings, "text">;

export function HeadingSettings(props: IHeadingSettingsProps) {
  const { id, settings } = props;
  const { t } = useTranslation();

  const { Option } = Select;

  const dispatch = useAppDispatch();

  const trackedSettings: TTrackedHeadingSettings = useMemo(() => {
    return {
      level: settings.level,
    };
  }, [settings.level]);

  const [updatedTrackedSettings, setUpdatedTrackedSettings] = useState<TTrackedHeadingSettings>({
    level: settings.level,
  });

  const levelOptions: IWebsiteBuilderPopupOption<WebsiteBuilderHeadingLevel>[] = useMemo(() => {
    return range(WebsiteBuilderHeadingLevel.ONE, WebsiteBuilderHeadingLevel.SIX + 1).map(level => ({
      label: `H${level}`,
      value: level,
    }));
  }, []);

  function dispatchSettings(prevSettings: TTrackedHeadingSettings, nextSettings: TTrackedHeadingSettings) {
    dispatch(
      websiteBuilderUpdateElementSettings({
        id,
        prevElementSettings: {
          type: "heading",
          settings: {
            ...cloneDeep(prevSettings),
            text: settings.text,
          },
        },
        nextElementSettings: {
          type: "heading",
          settings: {
            ...cloneDeep(nextSettings),
            text: settings.text,
          },
        },
      }),
    );
  }

  const { handleUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: trackedSettings,
    updatedState: updatedTrackedSettings,
    setUpdatedState: setUpdatedTrackedSettings,
    dispatchState: dispatchSettings,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  function renderLevelSection() {
    return (
      <Select
        label={t("Level")}
        onChange={(level: WebsiteBuilderHeadingLevel) =>
          handleUpdateState(prev => ({
            ...prev,
            level,
          }))
        }
        defaultValue={updatedTrackedSettings.level}
      >
        {renderWebsiteBuilderPopupOptions(levelOptions)}
      </Select>
    );
  }

  function renderFontSection() {
    return (
      <>
        <Select
          label={t("Font")}
          onChange={() => {}}
          defaultValue={""}
        >
          <Option value="Inter">Inter</Option>
        </Select>
        <WebsiteBuilderPopupRow>
          <Select
            label={t("Weight")}
            onChange={() => {}}
            defaultValue={""}
          >
            <Option value={500}>Semibold</Option>
          </Select>
          <Input
            label={t("Size (px)")}
            value={""}
            onChange={() => {}}
          />
        </WebsiteBuilderPopupRow>
      </>
    );
  }

  function renderOrientationSection() {
    return (
      <div className="website-builder-heading-settings-orientations">
        <FontAwesomeIcon className="heading-settings-orientation" icon={faAlignLeft} />
        <FontAwesomeIcon className="heading-settings-orientation" icon={faAlignCenter} />
        <FontAwesomeIcon className="heading-settings-orientation" icon={faAlignRight} />
      </div>
    );
  }

  return (
    <WebsiteBuilderPopupSections sections={[renderLevelSection(), renderFontSection(), renderOrientationSection()]} />
  );
}
